/**
 * WorkspaceTimezoneBanner - shown to the tenant owner only while the workspace
 * has no timezone. Until one is set every member sees times in UTC and new
 * schedules default to UTC (utils/timezone.ts). Dismissible for the browser
 * session; it comes back next session until a timezone is set.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Clock, X } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useWorkspaceTimezoneSet } from '@/utils/timezone'

const DISMISS_KEY = 'vharta-workspace-timezone-banner-dismissed'

function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

function writeDismissed() {
  try {
    window.sessionStorage.setItem(DISMISS_KEY, '1')
  } catch {
    // storage unavailable (private window, blocked site data): the banner just comes back
  }
}

export function WorkspaceTimezoneBanner() {
  const isOwner = useAuthStore((s) => !!s.user?.tenantId && s.user.role === 'tenant_owner')
  const isSet = useWorkspaceTimezoneSet()
  const [dismissed, setDismissed] = useState(readDismissed)

  if (!isOwner || isSet || dismissed) return null
  return (
    <div
      className="flex items-start gap-3 border-b border-warning-100 bg-warning-50 px-4 py-3 text-sm text-warning-700"
      role="status"
      data-testid="workspace-timezone-banner"
    >
      <Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <p className="flex-1">
        Your workspace has no timezone yet, so everyone sees times in UTC and new schedules default to UTC.{' '}
        <Link className="font-medium underline" to="/tenant/settings?tab=organization">
          Set the workspace timezone
        </Link>
        .
      </p>
      <button
        type="button"
        className="rounded p-0.5 hover:bg-warning-100"
        aria-label="Dismiss for this session"
        onClick={() => {
          writeDismissed()
          setDismissed(true)
        }}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  )
}
